import { CartAddOn } from "./CartAddOn";
import { FulfillmentType } from './ProductDelivery';
import { IGiftCardJson, IOrderGiftCard } from './GiftCard';
import { IOrderProductPromoOfferJson, IOrderPromoOfferJson, IThresholdPromoOffer } from './Promo';
import { IProductEffectiveInventory, IProductPrice, IProductWithAdditionalCharge, IWarranty, Prop65WarningType } from './Product';
import { IUpdateSubscriptionRequest } from "JS/services/ProductService";
import { LocalDeliveryEstimateErrorType } from './LocalDelivery';

export interface IQuickCartSummaryJson {
	cartQty: number;
	shipItemQty: number;
	getPickupQty: number;
	giftCardsInCart: IGiftCardJson[];
	productsInCart: IQuickCartProductJson[];
	potentialRewardsPoints: number;
	subtotal: number;
	hasPickupItems: boolean;
	hasItemsToShip: boolean;
	hasDeliveryItems: boolean;
}

export interface IQuickCartProductJson {
	blainNumber: string;
	groupID?: number;
	virtualURL: string;
	imageURL: string;
	imageUrlLarger: string;
	warrantyTotal: string;
	warrantyPrice: string;
	warrantyQty: number;
	warranty: IWarranty;
	totalSavings: string;
	unitPrice: number;
	productName: string;
	qty: number;
	vendorBrand: string;
	price: IProductPrice;
	attributes: IOrderProductAttributeValue[];
}

// Warranties
export interface IUpdateWarrantyRequest {
	blainNumber: string;
	quantity: number;
}

// Maps to OrderJson on the backend. Is this a duplicate?
export interface IOrderSummary {
	orderId: number;
	cartSummary: IQuickCartSummaryJson;
	products: IOrderProductJson[];
	cartQty: number;
	potentialRewardsPoints: number;
	currentRewardsShippingOffer: string;
	grandTotal: number;
	grandTotalString: string;
	remainingBalance: number;
	remainingBalanceString: string;
	governmentFeeLabel: string;
	governmentFeeString: string;
	salesTax: number;
	salesTaxString: string;
	hasSaleItems: boolean;
	savingsTotal: number;
	savingsTotalString: string;
	discountTotal: number;
	discounntTotalString: string;
	subtotal: number;
	subtotalString: string;
	shippingEstimateLine: string;
	shippingPromos: IOrderPromoOfferJson[];
	deliveryPromos: IOrderPromoOfferJson[];
	avalaraCustomerId?: number;
	appliedDiscounts: ISavingDetails[];
	savingDetails: ISavingDetails[];
	bouncebackOffers: IAppliedOffer[];
	thresholdPromos: IThresholdPromoOffer[];
	customerJson: IOrderCustomerJson;
	giftCards: IOrderGiftCard[];
	isEmpty: boolean;
	taxExemptProfileID: number;
	estimatedShipDate?: string;
	shipZip: string;
	estimatedShipDateString?: string;
	estimatedShippingArrivalDateString?: string;
	estimatedShippedGiftCardArrivalDateString?: string;
	shipCity: string;
	shipState: string;
	shipCityState: string;
	localDelivery: number;
	localDeliveryString: string;
	hasDeliveryItems: boolean;
	deliveryBlainNumbers: string[];
	hasItemsToShip: boolean;
	allItemsShipPromoExclude: boolean;
	hasGiftCardsToShip: boolean;
	shipBlainNumber: string[];
	hasPickupItems: boolean;
	pickupBlainNumber: string[];
	hasTaxExemptSelected: boolean;
	requiresCustomShippingQuote: boolean;
	hasLocalDeliveryError: boolean;
	localDeliveryError: LocalDeliveryEstimateErrorType;
	orderSummaryWarningMessage: string | null;
	highLevelAlerts: string[];
	isCustomOrder: boolean;
	isReplacementOrder:  boolean;
	shipMethod: string;
	hasParcelItems: boolean;
}

export interface IAppliedOffer {
	offerID: number;
	offerTitle: string;
	finePrint: string;
	hasDetails: boolean;
}

export interface ISavingDetails {
	description: string;
	amount: number;
}

export interface ICartInitResponse {
	orderSummary: IOrderSummary;
	cartAddons: CartAddOn[];
	success: boolean;
}

export interface ICartProductValues {
	itemID: number;
	itemQty: number;
	warrantySku: string;
	warrantyQty: number;
	name?: string;
	fulfillmentType: FulfillmentType;
	blainNumber: string;
	subscriptionRequest: IUpdateSubscriptionRequest | null;
}

export interface ICartGiftCardValues {
	itemID: number;
	itemQty: number;
}

export interface IZipcodeValidationResponse extends ICartUpdateResponse {
	shipCityState: string;
	warnings: CartUpdateResolutionType[];
}

export interface ICartAddProductResponse extends ICartUpdateResponse {
	success: boolean;
	blainNumber: string;
	unitPrice: string;
	qty: number;
	updateCartValues: IUpdateCartValues;
	cartContentHtmlString: string;
	cartOrderHtmlString: string;
	cddToCartModalHtmlString: string;
}

export interface ICartGiftCardUpdateResponse extends ICartUpdateResponse {
	giftCardValues: ICartGiftCardValues;
}

export interface ICartProductUpdateResponse extends ICartUpdateResponse {
	productValues: ICartProductValues;
}

export interface IUpdateCartValues {
	shipToZip: string;
	promoCode: string;
	taxExemptProfileID: number;
	avalaraCustomerID: number;
	products: ICartProductValues[];
	giftCards: ICartGiftCardValues[];
}

export interface ICartUpdateResponse {
	orderJson: IOrderSummary;
	cartSummary: IQuickCartSummaryJson;
	resolutionType: CartUpdateResolutionType;
	//Only expected when the resolution type is CartDeleted
	orderId: number;
}

export interface ICartGiftCardUpdateResponse extends ICartUpdateResponse {
	giftCardValues: ICartGiftCardValues;
}

export interface ICartProductUpdateResponse extends ICartUpdateResponse {
	productValues: ICartProductValues;
}

export interface ICartZipcodeUpdateResponse extends ICartUpdateResponse {
	warnings: CartUpdateResolutionType[];
}

export enum CartUpdateResolutionType {
	Success,
	CartNotFound,
	InvalidZip,
	CannotShip,
	CannotDeliver,
	ProductNotFound,
	GiftCardNotFound,
	InvalidPromoCode,
	MaxLines,
	CannotAdd,
	CartIsEmpty,
	NullPayload,
	NotAllowed,
	CartDeleted,
}

export interface IAddToCartRequest {
	blainNumber: string;
	qty: number;
	storeNum: number;
	fromCart: boolean;
};

export interface IOrderCustomerJson {
	isLoggedIn: boolean;
	isRewardsMember: boolean;
	hasRegistryOrList: boolean;
	taxExemptProfiles: ICertificateJson[];
	email: string;
}

export interface ICertificateJson {
	name: string;
	id: number;
}

export interface IOrderDiscountJson {
	id: number;
	description: string;
	amount: number;
}

export interface IOrderProductAttributeValue { // might make sense to pull this out of here when we redo cart/checkout
	orderProductID: number;
	blainNumber: string;
	attributeDescription: string;
	attributeValue: string;
}

export interface IOrderProductJson extends IProductWithAdditionalCharge {
	id: number;
	blainNumber: string;
	groupID?: number;
	virtualURL: string;
	imageURL: string;
	imageUrlLarger: string;
	imageURLWebP: string;
	warrantyTotal : string;
	warrantyPrice: string;
	warrantyQty: number;
	warranty: IWarranty;
	totalSavings: string;
	unitPrice: string;
	eachPrice: string;
	productName: string;
	qty: number;
	qtyMin?: number;
	qtyMax?: number;
	qtyInterval?: number;
	vendorBrand: string;
	price: IProductPrice;
	attributes: IOrderProductAttributeValue[];
	availableWarranties: IWarranty[];
	storeNum?: number;
	warrantyBlainNumber: string;
	onHand?: number;
	taxExempt: boolean;
	isPickupProduct: boolean;
	isShipProduct: boolean;
	pdpCustomText: string;
	registryItemID?: number;
	promoOffer: IOrderProductPromoOfferJson;
	hasLocalDeliverySpecialNote: boolean;
	specialOrderItemNotice: string;
	isFree: boolean;
	isRewardsMemberPrice: boolean;
	isSpecialOfferPrice: boolean;
	warrantyName: string;
	activeOnECommerce: boolean;
	activeInStorePickup: boolean;
	activeLocalDelivery: boolean;
	hasError: boolean; // instead of flag
	fulfillmentType?: FulfillmentType;
	inventory: IProductEffectiveInventory[];
	storeStock: IProductEffectiveInventory;
	shipStock: IProductEffectiveInventory;
	isSoldOut: boolean;
	ltl: string;
	notCarriedInStore: boolean;
	specialShippingNote: OrderProductSpecialShippingType;
	arrivalDateString: string; //arrival at store
	estimatedDeliveryDateString: string; //arrival at customer location
	categoryName: string;
	departmentName: string;
	subscriptionInfo: SubscriptionResource;
	prop65Warning: Prop65WarningType;
	isLiveAnimal: boolean;
}

export interface IUpdateFulfillmentOptionRequest {
	fulfillmentOption: FulfillmentType;
}

export enum OrderProductSpecialShippingType {
	LTL,
	ExcludeFromShipPromo,
	None
}

//Subscription types

export enum ProductSubscriptionStatus{
	Active = 0,
	Eligible = 1,
	Ineligible = 2,
	Pending = 3,
}

export type SubscriptionResource = IIneligbleSubscriptionResource | IActiveSubscriptionResource | IEligibleSubscriptionResource | IPendingSubscriptionResource;

export interface IIneligbleSubscriptionResource {
	status: ProductSubscriptionStatus.Ineligible,
}

export interface IActiveSubscriptionResource {
	status: ProductSubscriptionStatus.Active,
	nextReminder: string | null,
	quantity: number,
	frequency: number,
	subscriptionID: number,
	isDue: boolean,
	programFlagTitle: string,
	rewardsPointsMultiplier: number,
	deadlineDateShort: string | null,
}

export interface IEligibleSubscriptionResource{
	status: ProductSubscriptionStatus.Eligible,
	subscriptionBenefits: ProductSubscriptionBenefits[],
	frequencyOptions: number[],
	frequency: number,
	title: string,
	titleNotice: string,
	titleNoticeColor: string,
	programNameFull: string,
	nonRewardsFlagText: string,
	nonRewardsPromoText: string,
	cartSubtitle: string,
	rewardsPointsMultiplier: number,
};

export interface IPendingSubscriptionResource{
	status: ProductSubscriptionStatus.Pending,
	subscriptionBenefits: ProductSubscriptionBenefits[],
	frequencyOptions: number[],
	frequency: number,
	title: string,
	titleNotice: string,
	titleNoticeColor: string,
	programNameFull: string,
	nonRewardsFlagText: string,
	nonRewardsPromoText: string,
	cartSubtitle: string,
	rewardsPointsMultiplier: number,
};

export interface ProductSubscriptionBenefits {
	lineItem: string,
	boldText: string,
	shouldAdjustWithQuantity: boolean,
}